import React from "react";
import { useForm } from "@inertiajs/react";
import { router } from "@inertiajs/react";
import { Button } from "@/components/ui/button";

interface LineItem {
  id?: number;
  description: string;
  hours: string;
  price_per_hour: string;
  _destroy?: boolean;
}

interface InvoiceFormData {
  issue_date: string;
  due_date: string;
  subject: string;
  from_company: string;
  invoice_for: string;
  status: string;
  line_items_attributes: LineItem[];
}

interface Props {
  invoice: {
    id: number;
    issue_date: string;
    due_date: string;
    subject?: string;
    from_company: string;
    invoice_for: string;
    status: string;
    line_items: Array<{
      id: number;
      description: string;
      hours: number;
      price_per_hour: number;
    }>;
  };
}

export default function Edit({ invoice }: Props) {
  const { data, setData, put, processing, errors } = useForm<InvoiceFormData>({
    issue_date: invoice.issue_date,
    due_date: invoice.due_date,
    subject: invoice.subject || "",
    from_company: invoice.from_company,
    invoice_for: invoice.invoice_for,
    status: invoice.status || "draft",
    line_items_attributes: invoice.line_items.map((item) => ({
      id: item.id,
      description: item.description,
      hours: item.hours.toString(),
      price_per_hour: item.price_per_hour.toString(),
    })),
  });

  const addLineItem = () => {
    setData("line_items_attributes", [
      ...data.line_items_attributes,
      { description: "", hours: "", price_per_hour: "" },
    ]);
  };

  const removeLineItem = (index: number) => {
    const newLineItems = [...data.line_items_attributes];
    if (newLineItems[index].id) {
      newLineItems[index]._destroy = true;
    } else {
      newLineItems.splice(index, 1);
    }
    setData("line_items_attributes", newLineItems);
  };

  const updateLineItem = (
    index: number,
    field: keyof LineItem,
    value: string,
  ) => {
    const newLineItems = [...data.line_items_attributes];
    newLineItems[index] = { ...newLineItems[index], [field]: value };
    setData("line_items_attributes", newLineItems);
  };

  const calculateTotal = () => {
    return data.line_items_attributes
      .filter((item) => !item._destroy)
      .reduce((sum, item) => {
        const hours = parseFloat(item.hours) || 0;
        const pricePerHour = parseFloat(item.price_per_hour) || 0;
        return sum + hours * pricePerHour;
      }, 0);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    put(`/invoices/${invoice.id}`);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Edit Invoice</h1>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            Invoice Details
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label
                htmlFor="issue_date"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Issue Date
              </label>
              <input
                type="date"
                id="issue_date"
                value={data.issue_date}
                onChange={(e) => setData("issue_date", e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {errors.issue_date && (
                <span className="text-red-500 text-sm mt-1">
                  {errors.issue_date}
                </span>
              )}
            </div>

            <div>
              <label
                htmlFor="due_date"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Due Date
              </label>
              <input
                type="date"
                id="due_date"
                value={data.due_date}
                onChange={(e) => setData("due_date", e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {errors.due_date && (
                <span className="text-red-500 text-sm mt-1">
                  {errors.due_date}
                </span>
              )}
            </div>

              <div>
                <label
                  htmlFor="status"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Status
                </label>
                <select
                  id="status"
                  value={data.status}
                  onChange={(e) => setData("status", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="draft">Draft</option>
                  <option value="sent">Sent</option>
                  <option value="paid">Paid</option>
                  <option value="overdue">Overdue</option>
                </select>
                {errors.status && (
                  <span className="text-red-500 text-sm mt-1">
                    {errors.status}
                  </span>
                )}
              </div>
            </div>

          <div className="mt-6">
            <label
              htmlFor="subject"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Subject (Optional)
            </label>
            <input
              type="text"
              id="subject"
              value={data.subject}
              onChange={(e) => setData("subject", e.target.value)}
              placeholder="e.g., Website Development - Phase 1"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {errors.subject && (
              <span className="text-red-500 text-sm mt-1">
                {errors.subject}
              </span>
            )}
          </div>

          <div className="mt-6">
            <label
              htmlFor="from_company"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              From (Company Name)
            </label>
            <input
              type="text"
              id="from_company"
              value={data.from_company}
              onChange={(e) => setData("from_company", e.target.value)}
              required
              placeholder="Your Company Name"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {errors.from_company && (
              <span className="text-red-500 text-sm mt-1">
                {errors.from_company}
              </span>
            )}
          </div>

          <div className="mt-6">
            <label
              htmlFor="invoice_for"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Invoice For
            </label>
            <input
              type="text"
              id="invoice_for"
              value={data.invoice_for}
              onChange={(e) => setData("invoice_for", e.target.value)}
              required
              placeholder="Client Name or Company"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {errors.invoice_for && (
              <span className="text-red-500 text-sm mt-1">
                {errors.invoice_for}
              </span>
            )}
          </div>
        </div>

        <div className="bg-white shadow-sm rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Line Items</h2>
            <Button
              type="button"
              onClick={addLineItem}
              variant="secondary"
              size="sm"
            >
              Add Line Item
            </Button>
          </div>

          <div className="space-y-2">
            <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-700 pb-2 border-b">
              <div className="col-span-5">Description</div>
              <div className="col-span-2">Hours</div>
              <div className="col-span-2">Price/Hour</div>
              <div className="col-span-2">Total</div>
              <div className="col-span-1"></div>
            </div>

            {data.line_items_attributes.map((item, index) => {
              if (item._destroy) return null;
              const itemTotal =
                (parseFloat(item.hours) || 0) *
                (parseFloat(item.price_per_hour) || 0);
              return (
                <div key={index} className="grid grid-cols-12 gap-4 py-2">
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) =>
                      updateLineItem(index, "description", e.target.value)
                    }
                    placeholder="Description"
                    required
                    className="col-span-5 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="number"
                    step="0.01"
                    value={item.hours}
                    onChange={(e) =>
                      updateLineItem(index, "hours", e.target.value)
                    }
                    placeholder="0.00"
                    required
                    className="col-span-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="number"
                    step="0.01"
                    value={item.price_per_hour}
                    onChange={(e) =>
                      updateLineItem(index, "price_per_hour", e.target.value)
                    }
                    placeholder="0.00"
                    required
                    className="col-span-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="col-span-2 px-3 py-2 text-gray-900 font-medium">
                    ${itemTotal.toFixed(2)}
                  </div>
                  <Button
                    type="button"
                    onClick={() => removeLineItem(index)}
                    className="col-span-1"
                    variant="destructive"
                    size="sm"
                  >
                    Remove
                  </Button>
                </div>
              );
            })}
          </div>

          <div className="mt-6 pt-4 border-t flex justify-end">
            <div className="text-lg font-semibold text-gray-900">
              Amount Due:{" "}
              <span className="text-2xl">${calculateTotal().toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            onClick={() => router.visit(`/invoices/${invoice.id}`)}
            variant="outline"
          >
            Cancel
          </Button>
          <Button type="submit" disabled={processing}>
            {processing ? "Updating..." : "Update Invoice"}
          </Button>
        </div>
      </form>
    </div>
  );
}
